package com.rosario.juridico.domain

import com.rosario.juridico.platform.FirebaseProxy
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

/**
 * Resultado para consultas del cliente y ofertas recibidas.
 */
sealed class MisConsultasResult<out T> {
    data class Success<out T>(val data: T) : MisConsultasResult<T>()
    data class Error(val message: String, val cause: Throwable? = null) : MisConsultasResult<Nothing>()
}

/**
 * Repositorio responsable de consultar las publicaciones propias del cliente y las ofertas recibidas en Firestore.
 */
class MisConsultasClienteRepository {

    /**
     * Obtiene las consultas creadas por el cliente actualmente autenticado.
     */
    suspend fun getMisConsultas(): MisConsultasResult<List<ConsultaDataModel>> = withContext(Dispatchers.Default) {
        val userId = FirebaseProxy.getCurrentUserId()
            ?: return@withContext MisConsultasResult.Error("Sesión no válida. Inicia sesión para ver tus publicaciones.")

        try {
            val firestore = Firebase.firestore
            val snapshot = firestore.collection("consultas").get()

            val misConsultas = snapshot.documents
                .mapNotNull { doc ->
                    try {
                        doc.data<ConsultaDataModel>()
                    } catch (e: Exception) {
                        null
                    }
                }
                .filter { it.userId == userId }
                .sortedByDescending { it.fecha }

            MisConsultasResult.Success(misConsultas)
        } catch (e: Exception) {
            MisConsultasResult.Error(e.message ?: "Error al obtener tus consultas desde Firestore.", e)
        }
    }

    /**
     * Obtiene todas las ofertas recibidas para una consulta específica del cliente.
     */
    suspend fun getOfertasDeConsulta(consultaId: String): MisConsultasResult<List<OfertaDataModel>> = withContext(Dispatchers.Default) {
        try {
            val firestore = Firebase.firestore
            val subcollectionSnapshot = firestore.collection("consultas")
                .document(consultaId)
                .collection("ofertas")
                .get()

            val ofertas = subcollectionSnapshot.documents
                .mapNotNull { doc ->
                    try {
                        doc.data<OfertaDataModel>()
                    } catch (e: Exception) {
                        null
                    }
                }
                .sortedByDescending { it.fechaOfertaIso }

            MisConsultasResult.Success(ofertas)
        } catch (e: Exception) {
            MisConsultasResult.Error(e.message ?: "Error al cargar las ofertas recibidas.", e)
        }
    }
}
